<template>
    <two-column-layout>
        <card-container>
            <h2>{{ $t(i18n.company_info_step.title) }}</h2>
            <p>{{ $t(i18n.company_info_step.description) }}</p>

            <number-input-fragment
              :label="$t(i18n.input.upper_management_amount.label)"
              :description="$t(i18n.input.upper_management_amount.description)"
              :placeholder="$t(i18n.input.upper_management_amount.placeholder)"
              size="small"
              :min="1"
              :max="100"
              whole-number
              show-info
              v-model="form.upperManagementAmount"
              :is-invalid="isFieldInvalid($v.form.upperManagementAmount)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_UPPER_MANAGEMENT_AMOUNT')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.upperManagementAmount" />
                </template>
            </number-input-fragment>
            <fragment-separator></fragment-separator>

            <currency-input-fragment v-if="isFieldAnsweredPositively(companyProfile.employees)"
              :label="$t(i18n.input.uniform_salary.label)"
              :description="$t(i18n.input.uniform_salary.description)"
              size="small"
              show-info
              v-model="form.uniformSalary"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.uniformSalary)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_UNIFORM_SALARY')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.uniformSalary" />
                </template>
            </currency-input-fragment>
            <fragment-separator v-if="isFieldAnsweredPositively(companyProfile.employees)"></fragment-separator>

            <number-input-fragment
              :label="$t(i18n.input.non_salaried_employees.label)"
              :description="$t(i18n.input.non_salaried_employees.description)"
              :placeholder="$t(i18n.input.non_salaried_employees.placeholder)"
              size="small"
              :min="0"
              :max="100"
              whole-number
              show-info
              v-model="form.nonSalariedEmployees"
              :is-invalid="isFieldInvalid($v.form.nonSalariedEmployees)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_NON_SALARIED_EMPLOYEES')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.nonSalariedEmployees" />
                </template>
            </number-input-fragment>
            <fragment-separator></fragment-separator>

            <number-input-fragment
              :label="$t(i18n.input.driving_risks.label)"
              :description="$t(i18n.input.driving_risks.description)"
              :placeholder="$t(i18n.input.driving_risks.placeholder)"
              size="small"
              :min="1"
              :max="100"
              whole-number
              show-info
              v-model="form.drivingRisks"
              :is-invalid="isFieldInvalid($v.form.drivingRisks)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_DRIVING_RISKS')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.drivingRisks" />
                </template>
            </number-input-fragment>
            <fragment-separator></fragment-separator>

            <currency-input-fragment
              :label="$t(i18n.input.average_catalog_value_driving_risks.label)"
              :description="$t(i18n.input.average_catalog_value_driving_risks.description)"
              size="small"
              show-info
              v-model="form.averageCatalogValueDrivingRisks"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.averageCatalogValueDrivingRisks)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_AVERAGE_CATALOG_VALUE_DRIVING_RISKS')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.averageCatalogValueDrivingRisks" />
                </template>
            </currency-input-fragment>
            <fragment-separator v-if="isFieldAnsweredPositively(companyProfile.tradestockPresent)"></fragment-separator>

            <currency-input-fragment v-if="isFieldAnsweredPositively(companyProfile.tradestockPresent)"
              :label="$t(i18n.input.tradestock_max_value.label)"
              :description="$t(i18n.input.tradestock_max_value.description)"
              size="small"
              show-info
              v-model="form.tradestockMaxValue"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.tradestockMaxValue)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_TRADESTOCK_MAX_VALUE')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.tradestockMaxValue" />
                </template>
            </currency-input-fragment>
            <fragment-separator v-if="isFieldAnsweredPositively(companyProfile.tradestockPresent)"></fragment-separator>

            <currency-input-fragment v-if="isFieldAnsweredPositively(companyProfile.buildingOwnership)"
              :label="$t(i18n.input.company_building_rebuild_value.label)"
              :description="$t(i18n.input.company_building_rebuild_value.description)"
              size="small"
              show-info
              v-model="form.companyBuildingRebuildValue"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.companyBuildingRebuildValue)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_COMPANY_BUILDING_REBUILD_VALUE')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.companyBuildingRebuildValue" />
                </template>
            </currency-input-fragment>
            <fragment-separator v-if="isFieldAnsweredPositively(companyProfile.buildingOwnership)"></fragment-separator>

            <currency-input-fragment v-if="isFieldAnsweredPositively(companyProfile.inventoryPresent)"
              :label="$t(i18n.input.value_inventory.label)"
              :description="$t(i18n.input.value_inventory.description)"
              size="small"
              show-info
              v-model="form.valueInventory"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.valueInventory)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_VALUE_INVENTORY')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.valueInventory" />
                </template>
            </currency-input-fragment>
            <fragment-separator v-if="isFieldAnsweredPositively(companyProfile.inventoryPresent)"></fragment-separator>

            <currency-input-fragment v-if="isFieldAnsweredPositively(companyProfile.inventoryPresent)"
              :label="$t(i18n.input.value_goods.label)"
              :description="$t(i18n.input.value_goods.description)"
              size="small"
              show-info
              v-model="form.valueGoods"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.valueGoods)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_VALUE_GOODS')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.valueGoods" />
                </template>
            </currency-input-fragment>
            <fragment-separator v-if="isFieldAnsweredPositively(companyProfile.inventoryPresent)"></fragment-separator>

            <currency-input-fragment v-if="isFieldAnsweredPositively(companyProfile.inventoryPresent)"
              :label="$t(i18n.input.value_tires.label)"
              :description="$t(i18n.input.value_tires.description)"
              size="small"
              show-info
              v-model="form.valueTires"
              :precision="0"
              :is-invalid="isFieldInvalid($v.form.valueTires)"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_VALUE_TIRES')"
            >
                <template #result>
                    <field-error-messages :validation-state="$v.form.valueTires" />
                </template>
            </currency-input-fragment>

        </card-container>
        <button-group direction="right" top-spacing="l">
            <button-fragment icon-before="back"
                             as-link
                             @click="previousStep()">{{$t(i18n.button.previous)}}
            </button-fragment>
            <button-fragment @click="nextStep()">{{$t(i18n.button.next)}}</button-fragment>
        </button-group>
    </two-column-layout>
</template>

<script>
    import {Utils} from "@bvm/core";
    import {
        NumberInputFragment,
        CurrencyInputFragment,
        FragmentSeparator,
        TwoColumnLayout,
        ButtonFragment,
        ButtonGroup,
        CardContainer
    } from "@bvm/forms";
    import {
        minValue,
        maxValue,
        required,
        requiredIf,
        numeric,
        decimal
    } from 'vuelidate/lib/validators'
    import FieldErrorMessages from "../../shared/field-error-messages";
    import MediaItemModal from "../../shared/media-item-modal";
    import {createNamespacedHelpers} from "vuex";

    const { mapState, mapMutations } = createNamespacedHelpers(
      "gpsQuoteRequest"
    );

    const i18nBuilder = new Utils.I18n.I18nBuilder("gps_quote_request_form")
      .add("company_info_step", b => b.add("title").add("description"))
      .add("button", b => b.add("previous").add("next"))
      .add("input", b => b.add("upper_management_amount", c => c.add("label").add("description").add("placeholder"))
        .add("uniform_salary", c => c.add("label").add("description"))
        .add("non_salaried_employees", c => c.add("label").add("description").add("placeholder"))
        .add("driving_risks", c => c.add("label").add("description").add("placeholder"))
        .add("average_catalog_value_driving_risks", c => c.add("label").add("description"))
        .add("tradestock_max_value", c => c.add("label").add("description"))
        .add("company_building_rebuild_value", c => c.add("label").add("description"))
        .add("value_inventory", c => c.add("label").add("description"))
        .add("value_goods", c => c.add("label").add("description"))
        .add("value_tires", c => c.add("label").add("description")));

    const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
      .add("minValue")
      .add("maxValue")
      .add("numeric")
      .add("decimal")
      .add("required");

    export const messageKeys = [...i18nBuilder.getKeys(), ...i18nValidationBuilder.getKeys()];

    export default {
        name: "company-info-step",
        components: {
            FieldErrorMessages,
            NumberInputFragment,
            CurrencyInputFragment,
            FragmentSeparator,
            TwoColumnLayout,
            ButtonFragment,
            ButtonGroup,
            CardContainer
        },
        props: {},
        data() {
            return {
                i18n: i18nBuilder.getData(),
                form : {
                    upperManagementAmount : null,
                    uniformSalary : null,
                    nonSalariedEmployees : null,
                    drivingRisks : null,
                    averageCatalogValueDrivingRisks : null,
                    tradestockMaxValue : null,
                    companyBuildingRebuildValue : null,
                    valueInventory : null,
                    valueGoods : null,
                    valueTires : null
                }
            };
        },
        computed: {
            ...mapState(["companyInfo", "companyProfile"])
        },
        methods: {
            ...mapMutations(["setCompanyInfo"]),
            isFieldInvalid(field) {
                return field.$dirty && field.$invalid;
            },
            isFieldAnsweredPositively(fieldValue) {
                return fieldValue === 'y';
            },
            onInfoClicked(mediaItemTag) {
                this.$bvmModal.show(MediaItemModal, {
                    itemTag: mediaItemTag
                }, {
                    clickToClose: true
                });
            },
            previousStep(value) {
                this.$emit("previousStep", value);
            },
            updateState() {
                this.setCompanyInfo(this.form);
            },
            nextStep() {
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$emit("onError");
                } else {
                    this.updateState();
                    this.$emit("nextStep");
                }
            }
        },
        created() {
            this.form = this.companyInfo;
        },
        validations() {
            return {
                form: {
                    upperManagementAmount : {
                        numeric,
                        required,
                        minValue: minValue(1),
                        maxValue: maxValue(100)
                    },
                    uniformSalary : {
                        decimal,
                        required: requiredIf(() => this.isFieldAnsweredPositively(this.companyProfile.employees)),
                        minValue: minValue(1),
                        maxValue: maxValue(100000000) //100_000_000
                    },
                    nonSalariedEmployees : {
                        numeric,
                        required,
                        minValue: minValue(0),
                        maxValue: maxValue(100)
                    },
                    drivingRisks : {
                        numeric,
                        required,
                        minValue: minValue(1),
                        maxValue: maxValue(100)
                    },
                    averageCatalogValueDrivingRisks : {
                        decimal,
                        required,
                        minValue: minValue(1),
                        maxValue: maxValue(200000) //200_000
                    },
                    tradestockMaxValue : {
                        decimal,
                        required: requiredIf(() => this.isFieldAnsweredPositively(this.companyProfile.tradestockPresent)),
                        minValue: minValue(0),
                        maxValue: maxValue(10000000) //10_000_000
                    },
                    companyBuildingRebuildValue : {
                        decimal,
                        required: requiredIf(() => this.isFieldAnsweredPositively(this.companyProfile.buildingOwnership)),
                        minValue: minValue(0),
                        maxValue: maxValue(10000000) //10_000_000
                    },
                    valueInventory : {
                        decimal,
                        required: requiredIf(() => this.isFieldAnsweredPositively(this.companyProfile.inventoryPresent)),
                        minValue: minValue(1),
                        maxValue: maxValue(1000000) //1_000_000
                    },
                    valueGoods : {
                        decimal,
                        required: requiredIf(() => this.isFieldAnsweredPositively(this.companyProfile.inventoryPresent)),
                        minValue: minValue(1),
                        maxValue: maxValue(1000000) //1_000_000
                    },
                    valueTires : {
                        decimal,
                        v: requiredIf(() => this.isFieldAnsweredPositively(this.companyProfile.inventoryPresent)),
                        minValue: minValue(1),
                        maxValue: maxValue(1000000) //1_000_000
                    }
                }
            }
        }
    }
</script>

<style>

</style>